/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import { injectable } from '@patsnap/tahiti-core/shared/inversify';
import { autoUpdate } from '@floating-ui/dom';
import type { PopoverConfig, PopoverRenderer } from '../popover-registry';
import type { PopoverManager } from '../popover-manager';

@injectable()
export class PopoverManagerImpl implements PopoverManager {
    private _showTimeout: number | null = null;
    private _hideTimeout: number | null = null;
    private _currentPopovers = new Map<'hover' | 'click', {
        renderer: PopoverRenderer;
        element: HTMLElement;
        reference: HTMLElement;
        config: PopoverConfig;
        hideListeners: Array<() => void>;
        cleanup: () => void;
    }>();

    private _getPositioningStrategy(reference: HTMLElement, config: PopoverConfig) {
        if (typeof config.positioningStrategy === 'function') {
            return config.positioningStrategy(reference);
        }
        return config.positioningStrategy;
    }

    async showPopover(reference: HTMLElement, config: PopoverConfig) {
        const trigger = config.trigger as 'hover' | 'click';
        const currentPopover = this._currentPopovers.get(trigger);

        // 如果当前显示的是同一个 reference 的同类型 popover，则不做任何操作
        if (currentPopover?.reference === reference && currentPopover?.config === config) {
            // 清除可能的隐藏计时器
            if (this._hideTimeout) {
                clearTimeout(this._hideTimeout);
                this._hideTimeout = null;
            }
            return;
        }

        // 清除可能存在的定时器
        if (this._showTimeout) {
            clearTimeout(this._showTimeout);
            this._showTimeout = null;
        }
        if (this._hideTimeout) {
            clearTimeout(this._hideTimeout);
            this._hideTimeout = null;
        }

        const show = async () => {
            // 如果是 click 触发，则隐藏 hover 触发的 popover
            if (trigger === 'click') {
                await this.hidePopover('hover');
            }
            // 只隐藏同类型的当前popover
            if (currentPopover) {
                await this.hidePopover(trigger);
            }

            const next = () => {
                this.hidePopover(trigger);
            };
            const renderer = config.render(reference, next);
            if (!renderer?.mount) {
                console.warn(`Popover render function must return a mount function(selector: ${config.selector}).`);
                return;
            }
            const popoverElement = renderer.mount();

            popoverElement.style.position = 'absolute';
            const initialPosition = await this._getPositioningStrategy(reference, config).computePosition(reference, popoverElement);
            popoverElement.style.left = `${initialPosition.x}px`;
            popoverElement.style.top = `${initialPosition.y}px`;

            document.body.appendChild(popoverElement);

            if ('popover' in popoverElement) {
                try {
                    // @ts-ignore
                    popoverElement.popover = 'manual';
                    // @ts-ignore
                    popoverElement.showPopover();
                } catch (e) {
                    console.warn('Native popover API failed:', e);
                }
            }

            const updatePosition = async () => {
                const position = await this._getPositioningStrategy(reference, config).computePosition(reference, popoverElement);
                popoverElement.style.left = `${position.x}px`;
                popoverElement.style.top = `${position.y}px`;
            };

            const cleanup = autoUpdate(reference, popoverElement, updatePosition);

            const hideListeners: Array<() => void> = [];
            this._currentPopovers.set(trigger, {
                renderer,
                element: popoverElement,
                reference,
                config,
                hideListeners,
                cleanup,
            });

            this._setupHideConditions(trigger);
            config.onShow?.();
        };

        if (config.trigger === 'hover' && config.hoverDelay) {
            this._showTimeout = window.setTimeout(show, config.hoverDelay);
        } else {
            await show();
        }
    }

    private _setupHideConditions(trigger: 'hover' | 'click') {
        const currentPopover = this._currentPopovers.get(trigger);
        if (!currentPopover) return;

        const { config, element, hideListeners } = currentPopover;

        if (config.trigger === 'hover') {
            const intersectionObserver = new IntersectionObserver(
                (entries) => {
                    if (!entries[0].isIntersecting) {
                        this.hidePopover('hover');
                    }
                },
                { threshold: 0 },
            );

            // reference 节点发生变化时，也要移除 hover popover
            const mutationObserver = new MutationObserver(() => {
                this.hidePopover('hover');
            });

            mutationObserver.observe(currentPopover.reference, {
                childList: true,
                subtree: true,
                attributes: true,
            });

            hideListeners.push(() => mutationObserver.disconnect());

            intersectionObserver.observe(currentPopover.reference);
            hideListeners.push(() => intersectionObserver.disconnect());

            const onMouseLeave = () => {
                if (this._hideTimeout) return;

                // 添加延迟隐藏，以便在鼠标移动到 popover 上时可以取消隐藏
                this._hideTimeout = window.setTimeout(() => {
                    this.hidePopover('hover');
                    this._hideTimeout = null;
                }, config.hideDelay || 200); // 使用配置的延迟或默认值
            };

            // 添加鼠标进入 popover 的处理，取消隐藏计时器
            const onMouseEnter = () => {
                if (this._hideTimeout) {
                    clearTimeout(this._hideTimeout);
                    this._hideTimeout = null;
                }
            };

            element.addEventListener('mouseleave', onMouseLeave);
            element.addEventListener('mouseenter', onMouseEnter);
            currentPopover.reference.addEventListener('mouseleave', onMouseLeave);
            currentPopover.reference.addEventListener('mouseenter', onMouseEnter);

            hideListeners.push(
                () => {
                    element.removeEventListener('mouseleave', onMouseLeave);
                    element.removeEventListener('mouseenter', onMouseEnter);
                    currentPopover?.reference.removeEventListener('mouseleave', onMouseLeave);
                    currentPopover?.reference.removeEventListener('mouseenter', onMouseEnter);
                },
            );
        } else {
            const clickHandler = (e: MouseEvent) => {
                if (!element.contains(e.target as Node) &&
                    !currentPopover.reference.contains(e.target as Node)) {
                    this.hidePopover('click');
                }
            };

            // 使用 requestAnimationFrame 来延迟添加点击处理器，避免立即触发
            requestAnimationFrame(() => {
                document.addEventListener('click', clickHandler);
            });

            hideListeners.push(() => document.removeEventListener('click', clickHandler));
        }
    }

    async hideCurrentPopover() {
        // 隐藏所有popover
        await Promise.all(['hover', 'click'].map(trigger => this.hidePopover(trigger as 'hover' | 'click')));
    }

    private async hidePopover(trigger: 'hover' | 'click') {
        const currentPopover = this._currentPopovers.get(trigger);
        if (currentPopover) {
            const { config, element, hideListeners, cleanup } = currentPopover;

            const hide = () => {
                cleanup();

                // 清理所有监听器
                hideListeners.forEach(cleanup => cleanup());

                if ('popover' in element) {
                    try {
                        // @ts-ignore
                        element.hidePopover();
                    } catch (e) {
                        // 忽略可能的错误
                    }
                }

                element.remove();
                currentPopover.renderer.dispose();
                config.onHide?.();
                this._currentPopovers.delete(trigger);
            };

            if (config.trigger === 'hover' && config.hideDelay) {
                if (this._hideTimeout) clearTimeout(this._hideTimeout);
                this._hideTimeout = window.setTimeout(() => {
                    hide();
                    this._hideTimeout = null;
                }, config.hideDelay);
            } else {
                hide();
            }
        }
    }
}
